import { Icons } from "../Icons";
import { LastUsedBadge } from "../base-ui/LastUsedBadge";
import { ViewType } from "../types";
import { Button } from "@/components/ui/buttons/button";
import { LocalStorageKey, useLocalStorage } from "@/hooks/useLocalStorage";
import { StoredLastAuthMethod } from "@/lib/local-storage";
import { cn, safeAsync } from "@/lib/utils";
import { ProviderScopes } from "@supabase/auth-ui-shared";
import { Provider, SupabaseClient } from "@supabase/supabase-js";
import { Lock, Mail } from "lucide-react";
import { useState } from "react";

interface SocialAuthProps {
  supabaseClient: SupabaseClient;
  providers?: Provider[];
  providerScopes?: Partial<ProviderScopes>;
  queryParams?: { [key: string]: string };
  redirectTo?: string | undefined;
  view?: "sign_in" | "sign_up" | "magic_link";
  setAuthView?: (view: ViewType) => void;
  /** Rendered as a "Continue with Email" button on sign-up when provided. */
  onEmailPress?: () => void;
}

const PROVIDER_LABELS: Partial<Record<Provider, string>> = {
  google: "Google",
  azure: "Microsoft",
};

const PROVIDER_LAST_AUTH_METHODS: Partial<
  Record<Provider, StoredLastAuthMethod>
> = {
  google: "google",
  azure: "microsoft",
};

function SocialAuth({
  supabaseClient,
  providers = ["google", "azure"],
  providerScopes,
  queryParams,
  redirectTo,
  view = "sign_in",
  setAuthView,
  onEmailPress,
}: SocialAuthProps) {
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [lastAuthMethod] = useLocalStorage(
    LocalStorageKey.LastAuthMethod,
    null
  );

  const isCream = view === "sign_up";
  // The badge is a sign-in affordance; the cream sign-up layout doesn't show it.
  const badgeMethod = isCream ? null : lastAuthMethod;

  const buttonClass = cn(
    "relative w-full gap-2 px-2 text-sm font-normal",
    isCream
      ? "h-12 rounded-none border-[#e2dccf] bg-[#fbf9f4] hover:border-[#7586d7] hover:bg-[#fbf9f4]"
      : "h-10 rounded-lg border-surface-quatenary bg-surface-highlight hover:border-content-tertiary hover:bg-surface-highlight"
  );
  const mutedTextClass = isCream ? "text-[#7586d7]" : "text-content-tertiary";
  const strongTextClass = isCream ? "text-blue-500" : "text-content-primary";

  const handleProviderSignIn = async (provider: Provider) => {
    setIsOAuthLoading(true);
    const { error } = await supabaseClient.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
        scopes: providerScopes?.[provider],
        queryParams,
      },
    });
    if (error) {
      console.error(error);
    }
    setIsOAuthLoading(false);
  };

  if (providers.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-3">
      {providers.map((provider) => (
        <Button
          key={provider}
          type="button"
          variant="outline"
          disabled={isOAuthLoading}
          onClick={safeAsync(() => handleProviderSignIn(provider))}
          className={buttonClass}
        >
          <Icons provider={provider} />
          <span className={mutedTextClass}>
            Continue with{" "}
            <span className={strongTextClass}>
              {PROVIDER_LABELS[provider] ?? provider}
            </span>
          </span>
          {badgeMethod === PROVIDER_LAST_AUTH_METHODS[provider] && (
            <LastUsedBadge />
          )}
        </Button>
      ))}
      <Button
        type="button"
        variant="outline"
        disabled={isOAuthLoading}
        onClick={() => setAuthView?.("sso")}
        className={buttonClass}
      >
        <Lock className="h-4 w-4 shrink-0 text-blue-500" />
        <span className={mutedTextClass}>
          Continue with <span className={strongTextClass}>SAML SSO</span>
        </span>
        {badgeMethod === "saml_sso" && <LastUsedBadge />}
      </Button>
      {onEmailPress && (
        <Button
          type="button"
          variant="outline"
          onClick={onEmailPress}
          className={buttonClass}
        >
          <Mail className="h-4 w-4 shrink-0 text-blue-500" />
          <span className={mutedTextClass}>
            Continue with <span className={strongTextClass}>Email</span>
          </span>
        </Button>
      )}
    </div>
  );
}

export { SocialAuth };
